import { useState, type ReactNode } from 'react';
import { shortPersonName } from './person_name.js';
import type { PersonItem } from './person_row.js';
import {
  TravelerPicker,
  TravelerPickerField,
  travelerTrigger,
  type TravelerPickerCopy,
} from './traveler_picker.js';
import type { TravelerFieldSearch } from './who_search.js';

/** A lista do QUEM de uma pessoa só: a cópia do seletor de pessoas, menos o
 *  rótulo (é o do campo). */
export type TravelerSingleCopy = Omit<TravelerPickerCopy, 'label'> & {
  readonly backLabel: string;
};

/**
 * O QUEM de uma pessoa só (o condutor do carro, o dono do reembolso): o seletor
 * de pessoas compartilhado (aéreo, ônibus, hotel) em modo ÚNICO. Escolher
 * alguém já fecha, como a lista da política dos outros campos, por isso não há
 * pé com «Pronto». O solicitante fixo no topo aparece no gatilho como `you`.
 */
export function TravelerSingleField({
  person,
  label,
  search,
  copy,
  disabled = false,
  locale,
  header,
  onChange,
}: {
  readonly person: PersonItem;
  readonly label: string;
  readonly search: TravelerFieldSearch;
  readonly copy: TravelerSingleCopy;
  readonly disabled?: boolean;
  readonly locale?: string;
  /** Acima da busca, como no `TravelerPicker`. */
  readonly header?: ReactNode;
  readonly onChange: (person: PersonItem) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const summary = person.id === search.pinned?.id ? copy.you : shortPersonName(person.name, locale);
  return (
    <TravelerPickerField
      open={open}
      onOpenChange={setOpen}
      title={label}
      backLabel={copy.backLabel}
      trigger={travelerTrigger({
        people: [person],
        summary,
        label,
        expanded: open,
        disabled,
        ...(locale ? { locale } : {}),
        onOpen: () => {
          setOpen(!open);
        },
      })}
    >
      <TravelerPicker
        mode="single"
        items={search.items}
        selected={[person]}
        {...(search.pinned ? { pinned: search.pinned } : {})}
        searchable={search.searchable}
        query={search.query}
        searchState={search.state}
        hasMore={search.hasMore}
        copy={{ ...copy, label }}
        {...(locale ? { locale } : {})}
        {...(header ? { header } : {})}
        onQueryChange={search.onQueryChange}
        {...(search.onLoadMore ? { onLoadMore: search.onLoadMore } : {})}
        onSelect={(picked) => {
          setOpen(false);
          onChange(picked);
        }}
      />
    </TravelerPickerField>
  );
}
